import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, FileX, Flag, Gavel, History, Paperclip, RotateCcw, Stamp, Undo2, Upload,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { EmptyState, SectionCard, TableShell, Th } from "../ui";
import {
  useClauseRevertImpact, useDecisionMutations, useDecisions, useSelectableOffices,
} from "../../hooks";
import {
  DECISION_KIND_LABELS, ITEM_OUTCOME_OPTIONS, type AdministrativeDecisionRow, type DecisionKind,
} from "../../types";
import { apiErr, fmtDate } from "../../utils";
import { CaseLinkPicker, type CaseLink } from "./CaseLinkPicker";
import { InfoHint } from "./InfoHint";

const FINALITY_COLORS = { not_final: "yellow", clause_requested: "blue", final: "green" } as const;

/**
 * Rejestr decyzji projektu: metadane otrzymanych decyzji + status ostateczności.
 * Klauzula nadana tutaj natychmiast odblokowuje decyzję jako załącznik (odrolnienie,
 * lokalizacyjny) i domyka wniosek typu „Klauzula ostateczności”.
 */
export function DecisionsPanel({ connectionId, projectId }: { connectionId: string; projectId?: string }) {
  const { data: decisions, isLoading } = useDecisions({ connection: connectionId });
  const m = useDecisionMutations();
  const { data: offices } = useSelectableOffices();
  const [formOpen, setFormOpen] = useState(false);
  const [clauseFor, setClauseFor] = useState<AdministrativeDecisionRow | null>(null);
  const [revertFor, setRevertFor] = useState<AdministrativeDecisionRow | null>(null);
  // Podpięcie decyzji pod sprawę z Korespondencji (jako pozycja „odpowiedź").
  const [attachFor, setAttachFor] = useState<AdministrativeDecisionRow | null>(null);

  const [sign, setSign] = useState("");
  const [kind, setKind] = useState<DecisionKind>("ulicp");
  const [issuingOffice, setIssuingOffice] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const [clauseDate, setClauseDate] = useState(new Date().toISOString().slice(0, 10));
  const [clauseFile, setClauseFile] = useState<File | null>(null);

  const createDecision = () => {
    m.create.mutate(
      {
        connection: connectionId, sign, kind,
        issuing_office: issuingOffice || null,
        issued_at: issuedAt || null, description,
      },
      {
        onSuccess: (decision) => {
          if (file) m.uploadFile.mutate({ id: decision.id, file });
          setFormOpen(false);
          setSign(""); setDescription(""); setFile(null);
        },
      },
    );
  };

  return (
    <SectionCard
      title={
        <span className="flex items-center gap-1">
          Rejestr decyzji
          <InfoHint text="Metadane decyzji otrzymanych od urzędów: znak, organ, data, plik i STATUS OSTATECZNOŚCI. Decyzję dodasz ręcznie tutaj albo jednym klikiem z odpowiedzi w sprawie (zakładka Sprawy). Ostateczna decyzja (z klauzulą) automatycznie nadaje się jako załącznik do odrolnienia i wniosków lokalizacyjnych. Przycisk ze stemplem = odnotuj nadaną klauzulę (data + skan)." />
        </span>
      }
      action={<Button size="sm" onClick={() => setFormOpen(true)}>+ Dodaj decyzję</Button>}
    >
      {isLoading ? null : !decisions?.length ? (
        <EmptyState>Brak zarejestrowanych decyzji</EmptyState>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Znak</Th><Th>Rodzaj</Th><Th>Organ</Th><Th>Wydana</Th><Th>Ostateczność</Th><Th>Plik</Th><Th>Sprawa</Th><Th> </Th>
            </tr>
          </thead>
          <tbody>
            {decisions.map((d) => (
              <tr key={d.id} className="border-t border-[var(--color-border)]">
                <td className="px-2 py-1.5 font-mono">{d.sign}</td>
                <td className="px-2 py-1.5">{d.kind_display}</td>
                <td className="px-2 py-1.5">{d.issuing_display || "—"}</td>
                <td className="px-2 py-1.5">{fmtDate(d.issued_at)}</td>
                <td className="px-2 py-1.5">
                  <Badge color={FINALITY_COLORS[d.finality]}>{d.finality_display}</Badge>
                  {d.final_at && <span className="ml-1 text-[10px]">{fmtDate(d.final_at)}</span>}
                </td>
                <td className="px-2 py-1.5">
                  {d.file?.url ? (
                    <a href={d.file.url} download className="hover:underline text-xs font-mono">PDF</a>
                  ) : "—"}
                </td>
                <td className="px-2 py-1.5">
                  {d.proceeding_info ? (
                    <Link
                      to={`/power-projects/${projectId ?? ""}?tab=correspondence&proceeding=${d.proceeding_info.id}`}
                      className="text-xs hover:underline"
                      title="Decyzja siedzi w tej sprawie jako pozycja „odpowiedź” — otwórz w zakładce Sprawy"
                    >
                      {d.proceeding_info.type_name}{" "}
                      <Badge color={d.proceeding_info.status === "closed" ? "gray" : "yellow"}>
                        {d.proceeding_info.status === "closed" ? "zamknięta" : "otwarta"}
                      </Badge>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                      title="Dodaj decyzję do sprawy z Korespondencji jako pozycję „odpowiedź”"
                      onClick={() => setAttachFor(d)}
                    >
                      <Gavel size={12} /> Podepnij do sprawy
                    </button>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  {d.finality !== "final" ? (
                    <button
                      className="text-[var(--color-text-muted)] hover:text-green-700"
                      title="Odnotuj nadaną klauzulę ostateczności"
                      onClick={() => setClauseFor(d)}
                    >
                      <Stamp size={14} />
                    </button>
                  ) : (
                    <button
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                      title="Cofnij ostateczność (klauzula odnotowana przez pomyłkę)"
                      onClick={() => setRevertFor(d)}
                    >
                      <Undo2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}

      {formOpen && (
        <Modal open onClose={() => setFormOpen(false)} title="Dodaj decyzję do rejestru">
          <div className="space-y-3">
            <label className="block">
              <span className="section-label">Znak / numer decyzji *</span>
              <input className="input-base mt-1 font-mono" value={sign} onChange={(e) => setSign(e.target.value)} />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Rodzaj</span>
                <select className="input-base mt-1" value={kind} onChange={(e) => setKind(e.target.value as DecisionKind)}>
                  {Object.entries(DECISION_KIND_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="section-label">Data wydania</span>
                <DatePicker className="mt-1" value={issuedAt} onChange={setIssuedAt} />
              </label>
            </div>
            <label className="block">
              <span className="section-label">Organ wydający</span>
              <SearchableSelect
                options={(offices ?? []).map((o) => ({ value: o.id, label: o.name }))}
                value={issuingOffice}
                placeholder="Wybierz urząd…"
                onChange={(e) => setIssuingOffice(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="section-label">Czego dotyczy</span>
              <input className="input-base mt-1" value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <div>
              <span className="section-label">Plik decyzji (PDF)</span>
              <FileDropZone className="mt-1" accept=".pdf"
                            files={file ? [file] : []}
                            onFilesChange={(fs) => setFile(fs[0] ?? null)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setFormOpen(false)}>Anuluj</Button>
              <Button onClick={createDecision} loading={m.create.isPending} disabled={!sign}>Dodaj</Button>
            </div>
          </div>
        </Modal>
      )}

      {clauseFor && (
        <Modal open onClose={() => setClauseFor(null)} title={`Klauzula ostateczności — ${clauseFor.sign}`}>
          <div className="space-y-3">
            <div className="text-xs text-[var(--color-text-secondary)]">
              Odnotowanie klauzuli robi decyzję OSTATECZNĄ — od razu nadaje się jako załącznik
              do odrolnienia i wniosków lokalizacyjnych.
            </div>
            <label className="block">
              <span className="section-label">Data ostateczności *</span>
              <DatePicker className="mt-1" value={clauseDate}
                     onChange={setClauseDate} />
            </label>
            <div>
              <span className="section-label">Skan ostemplowanej decyzji</span>
              <FileDropZone className="mt-1" accept=".pdf"
                            files={clauseFile ? [clauseFile] : []}
                            onFilesChange={(fs) => setClauseFile(fs[0] ?? null)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setClauseFor(null)}>Anuluj</Button>
              <Button
                loading={m.markClause.isPending}
                onClick={() =>
                  m.markClause.mutate(
                    { id: clauseFor.id, finalAt: clauseDate, file: clauseFile ?? undefined },
                    { onSuccess: () => setClauseFor(null) },
                  )
                }
              >
                <Upload size={13} className="mr-1" /> Odnotuj klauzulę
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {revertFor && (
        <ClauseRevertModal decision={revertFor} onClose={() => setRevertFor(null)} />
      )}
      {attachFor && (
        <AttachDecisionModal decision={attachFor} connectionId={connectionId} onClose={() => setAttachFor(null)} />
      )}
    </SectionCard>
  );
}

/**
 * Decyzja → sprawa z Korespondencji jako pozycja „odpowiedź" (znak decyzji, data wydania,
 * plik). Jedna decyzja siedzi w jednej sprawie (backend odbija drugą próbę — także gdy
 * decyzję zarejestrowano z pozycji sprawy), tylko w obrębie tego samego projektu.
 */
function AttachDecisionModal({ decision, connectionId, onClose }: {
  decision: AdministrativeDecisionRow;
  connectionId: string;
  onClose: () => void;
}) {
  const m = useDecisionMutations();
  const [link, setLink] = useState<CaseLink | null>(null);
  const [closes, setCloses] = useState(false);
  const [outcome, setOutcome] = useState("positive");
  const [note, setNote] = useState("");

  return (
    <Modal open onClose={onClose} title={`Podepnij decyzję ${decision.sign} do sprawy`}>
      <div className="space-y-3">
        <p className="text-xs text-[var(--color-text-secondary)]">
          Decyzja trafi do wybranej sprawy jako pozycja „Odpowiedź” (znak {decision.sign}
          {decision.issued_at ? `, wpływ ${fmtDate(decision.issued_at)}` : ""}
          {decision.file ? ", z plikiem decyzji" : ""}). Tylko sprawy tego projektu.
        </p>
        <div>
          <span className="section-label">Sprawa (otwarta)</span>
          <div className="mt-1">
            <CaseLinkPicker
              connectionId={connectionId}
              recipientOffice={decision.issuing_office}
              mode="decision"
              value={link}
              onChange={setLink}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 font-mono text-xs">
          <input type="checkbox" checked={closes} onChange={(e) => setCloses(e.target.checked)} />
          Decyzja zamyka sprawę (rozstrzygnięcie)
        </label>
        {closes && (
          <label className="block sm:max-w-xs">
            <span className="section-label">Rozstrzygnięcie</span>
            <select className="input-base mt-1" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              {ITEM_OUTCOME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        )}
        <label className="block">
          <span className="section-label">Notatka pozycji</span>
          <input
            className="input-base mt-1"
            placeholder={`Decyzja ${decision.sign} — ${decision.kind_display}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        {m.attachToProceeding.error && (
          <p className="text-xs text-red-600">{apiErr(m.attachToProceeding.error)}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button
            disabled={!link}
            loading={m.attachToProceeding.isPending}
            onClick={() => link && m.attachToProceeding.mutate(
              {
                id: decision.id, proceeding: link.proceeding, closes_thread: closes,
                outcome: closes ? outcome : undefined, note: note.trim() || undefined,
              },
              { onSuccess: onClose },
            )}
          >
            <Gavel size={13} className="mr-1" /> Podepnij do sprawy
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Jedna konsekwencja cofnięcia — ikona + tytuł + treść. */
function ImpactRow({ icon, title, tone = "neutral", children }: {
  icon: React.ReactNode;
  title: string;
  tone?: "neutral" | "warn";
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 px-3 py-2.5 border border-[var(--color-border)] bg-[var(--color-bg-base)]">
      <div className={tone === "warn" ? "text-[var(--color-primary)] mt-0.5" : "text-[var(--color-text-muted)] mt-0.5"}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="section-label mb-0.5">{title}</div>
        <div className="text-xs text-[var(--color-text-secondary)] space-y-1">{children}</div>
      </div>
    </div>
  );
}

/**
 * Modal potwierdzenia cofnięcia klauzuli ostateczności. Pokazuje POLICZONE skutki
 * (wnioski, które stracą decyzję jako załącznik, kamień Kontroli Procesu, powrót na
 * listę „do obsłużenia”), a nie ogólniki — cofnięcie jest świadomą decyzją, nie klikiem.
 */
function ClauseRevertModal({ decision, onClose }: {
  decision: AdministrativeDecisionRow;
  onClose: () => void;
}) {
  const { data: impact, isLoading } = useClauseRevertImpact(decision.id);
  const m = useDecisionMutations();
  const [reason, setReason] = useState("");
  const [understood, setUnderstood] = useState(false);

  return (
    <Modal open onClose={onClose} size="lg" title={`Cofnięcie ostateczności — ${decision.sign}`}>
      <div className="space-y-3">
        <div className="flex gap-3 px-3 py-2.5 border border-[var(--color-primary)] bg-[var(--color-primary-dim)]">
          <AlertTriangle size={16} className="text-[var(--color-primary)] mt-0.5 shrink-0" />
          <div className="text-xs text-[var(--color-text-primary)]">
            Decyzja <span className="font-mono font-bold">{decision.sign}</span> ({decision.kind_display})
            przestanie być OSTATECZNA{decision.final_at ? ` — klauzula z ${fmtDate(decision.final_at)} zostanie usunięta` : ""}.
            Cofaj tylko wtedy, gdy klauzula została odnotowana przez pomyłkę — nie wtedy, gdy urząd
            faktycznie ją uchylił (to jest osobna, nowa decyzja do rejestru).
          </div>
        </div>

        {isLoading || !impact ? (
          <div className="text-xs text-[var(--color-text-muted)] px-1 py-4">Liczę konsekwencje…</div>
        ) : (
          <div className="space-y-2">
            <ImpactRow icon={<RotateCcw size={14} />} title="Rejestr decyzji">
              <div>
                Status wróci na <span className="font-mono">„Nieostateczna”</span>, data ostateczności zostanie wyczyszczona.
              </div>
              {impact.clause_file && (
                <div className="text-[var(--color-primary)]">
                  Skan klauzuli <span className="font-mono">{impact.clause_file}</span> zostanie odpięty od decyzji.
                </div>
              )}
              {!!impact.parcels.length && (
                <div>Dotyczy działek: <span className="font-mono">{impact.parcels.join(", ")}</span>.</div>
              )}
            </ImpactRow>

            <ImpactRow
              icon={impact.applications.length ? <FileX size={14} /> : <Paperclip size={14} />}
              title="Wnioski, w których decyzja jest załącznikiem"
              tone={impact.applications.some((a) => a.already_sent || !a.replacement) ? "warn" : "neutral"}
            >
              {impact.applications.length === 0 ? (
                <div>Żaden wniosek nie korzysta z tej decyzji jako automatycznego załącznika.</div>
              ) : (
                impact.applications.map((a) => (
                  <div key={a.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="font-mono">{a.number}</span>
                    <span>{a.type_label}</span>
                    <Badge color={a.already_sent ? "yellow" : "gray"}>{a.status_display}</Badge>
                    {a.replacement ? (
                      <span className="text-[var(--color-text-muted)]">
                        → wskoczy inna ostateczna decyzja ({a.replacement})
                      </span>
                    ) : (
                      <span className="text-[var(--color-primary)]">→ checklista przestanie być kompletna</span>
                    )}
                  </div>
                ))
              )}
              {impact.applications.some((a) => a.already_sent) && (
                <div className="text-[var(--color-primary)]">
                  Uwaga: wniosek jest już złożony — cofnięcie nie wycofuje wysłanych papierów, zmienia tylko stan w systemie.
                </div>
              )}
            </ImpactRow>

            {impact.milestone && (
              <ImpactRow icon={<Flag size={14} />} title="Kontrola Procesu" tone={impact.milestone.kept_by ? "neutral" : "warn"}>
                {impact.milestone.kept_by ? (
                  <div>
                    Kamień <span className="font-mono">{impact.milestone.label}</span> zostaje domknięty przez
                    inną ostateczną decyzję (<span className="font-mono">{impact.milestone.kept_by}</span>).
                  </div>
                ) : (
                  <div>
                    Zniknie propozycja domknięcia kamienia <span className="font-mono">{impact.milestone.label}</span>.
                    Jeśli kamień został już ustawiony ręcznie, zostaje — trzeba go cofnąć osobno na tablicy.
                  </div>
                )}
              </ImpactRow>
            )}

            <ImpactRow icon={<Stamp size={14} />} title="Wniosek o klauzulę">
              <div>
                Decyzja wróci na listę „do obsłużenia” jako wymagająca klauzuli ostateczności.
              </div>
              {impact.clause_application && (
                <div>
                  Istniejący wniosek o klauzulę: <span className="font-mono">{impact.clause_application.number}</span>{" "}
                  ({impact.clause_application.status_display}) — pozostaje bez zmian.
                </div>
              )}
            </ImpactRow>

            <ImpactRow icon={<History size={14} />} title="Ślad">
              <div>
                W historii każdej powiązanej działki zapisze się kto i kiedy cofnął ostateczność (wraz z powodem).
              </div>
            </ImpactRow>
          </div>
        )}

        <label className="block">
          <span className="section-label">Powód cofnięcia (trafi do historii działek)</span>
          <input
            className="input-base mt-1"
            placeholder="np. klauzula odnotowana przez pomyłkę na złej decyzji"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>

        <label className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
          />
          <span>Rozumiem powyższe konsekwencje i chcę cofnąć ostateczność tej decyzji.</span>
        </label>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button
            variant="danger"
            disabled={!understood || isLoading}
            loading={m.clearClause.isPending}
            onClick={() =>
              m.clearClause.mutate({ id: decision.id, reason }, { onSuccess: onClose })
            }
          >
            <Undo2 size={13} className="mr-1" /> Cofnij ostateczność
          </Button>
        </div>
      </div>
    </Modal>
  );
}
